<template>
  <!-- 碳盘查问卷填报 -->
  <div>
    <div class="back" v-if="isVisible"></div>
    <transition name="slide">
      <div class="excel_container" v-if="isVisible">
        <div class="excel_header_view">
          <!-- 问卷头部 -->
          <div class="qn-header">
            <div class="qn-header-top">
              <div class="qn-agent-tag">
                <img src="@/assets/images/chat_tpc.png" alt="" />
                <span>碳盘查Agent</span>
              </div>
              <div class="excel_header_actions">
                <div v-if="pendingCount" class="qn-pending-tag">{{ pendingCount }}类待确认</div>
                <i class="el-icon-circle-close" @click="hideModal"></i>
              </div>
            </div>
            <div class="qn-title">{{ formData.title }}</div>
            <div class="qn-subtitle">{{ formData.subTitle }}</div>
            <div class="qn-progress">
              <div class="qn-progress-meta">
                <span>填报进度</span>
                <span class="qn-progress-num">{{ progressText }}</span>
              </div>
              <div class="qn-progress-track">
                <div class="qn-progress-bar" :style="{ width: progressPercent + '%' }"></div>
              </div>
            </div>
          </div>

          <!-- 问卷主体 -->
          <div class="qn-body">
            <div v-if="loading" class="qn-loading">
              <i class="el-icon-loading"></i>
              <span>问卷加载中...</span>
            </div>
            <div
              class="qn-card"
              v-for="(item, index) in formData.items"
              :key="index"
            >
              <div class="qn-card-head">
                <div class="qn-card-head-left">
                  <span class="qn-category" :class="{ active: item.is_expanded }">{{ item.category }}</span>
                </div>
                <div class="qn-expand" @click="toggleExpand(item)">
                  <span>查看排放源识别结果</span>
                  <i :class="item.is_expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
                </div>
              </div>
              <div class="qn-card-title">{{ item.title }}</div>
              <div class="qn-card-hint">识别到 {{ (item.identified_sources || []).length }} 项排放源</div>
              <div class="qn-options">
                <div
                  v-for="option in item.options"
                  :key="option.value"
                  class="qn-option"
                  :class="{ selected: item.selected_option === option.value }"
                  @click="selectOption(item, option.value)"
                >
                  <div class="qn-option-label">{{ option.label }}</div>
                  <div class="qn-option-desc">{{ option.description }}</div>
                </div>
              </div>
              <div class="qn-sources" v-if="item.is_expanded">
                <div class="qn-sources-title">
                  <span class="qn-ai-icon">
                    <svg viewBox="0 0 16 16" width="14" height="14">
                      <path fill="currentColor" d="M8 1.2l.9 2.6L12 5l-3.1 1.2L8 8.8 7.1 6.2 4 5l3.1-1.2L8 1.2zm5.2 6.3l.5 1.5 1.6.6-1.6.6-.5 1.5-.5-1.5-1.6-.6 1.6-.6.5-1.5zM2.8 8.8l.5 1.4 1.5.6-1.5.6-.5 1.4-.5-1.4-1.5-.6 1.5-.6.5-1.4z"/>
                    </svg>
                  </span>
                  <span>AI识别的具体排放源</span>
                </div>
                <div class="qn-sources-sub">根据您上传的材料识别到以下排放源：</div>
                <div v-if="!(item.identified_sources || []).length" class="qn-sources-empty">暂未识别到排放源</div>
                <div
                  v-for="(source, sIndex) in item.identified_sources"
                  :key="sIndex"
                  class="qn-source-row"
                >
                  <div class="qn-source-main">
                    <div class="qn-source-label">{{ formatSourceLabel(source.label) }}</div>
                    <div class="qn-source-data">{{ source.data_source }}</div>
                  </div>
                  <div class="qn-source-status" :class="source.status">{{ statusText(source.status) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 问卷底部 -->
          <div class="qn-footer">
            <div class="qn-btn qn-btn-ghost" @click="resetForm">
              <i class="el-icon-refresh"></i>
              <span>重新填报</span>
            </div>
            <div class="qn-btn qn-btn-primary" :class="{ 'is-submitting': isSubmitting }" @click="submitForm">
              <i class="el-icon-upload2"></i>
              <span>{{ isSubmitting ? '提交中' : '提交' }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getTpcQuestionnaire, putTpcQuestionnaire } from '@/api/index';

export default {
  name: 'QuestionNaire',
  data() {
    return {
      isVisible: false, // 是否显示问卷
      questionnaireId: '', // 问卷ID
      loading: false, // 是否加载中
      isSubmitting: false, // 是否提交中
      formData: { // 问卷数据
        title: '',
        subTitle: '',
        items: [],
      },
      originalData: {}, // 原始数据
    };
  },
  computed: {
    // 问卷项
    items() {
      return this.formData.items || [];
    },
    // 已回答项
    answeredCount() {
      return this.items.filter(item => item && item.selected_option).length;
    },
    // 待确认项
    pendingCount() {
      return this.items.length;
    },
    // 进度文本
    progressText() {
      return `${this.answeredCount}/${this.items.length}`;
    },
    // 进度百分比
    progressPercent() {
      if (!this.items.length) return 0;
      return Math.min(100, (this.answeredCount / this.items.length) * 100);
    },
  },
  methods: {
    // 显示弹窗
    showModal(questionnaireId) {
      this.questionnaireId = questionnaireId;
      this.isVisible = true;
      this.loadData(questionnaireId);
      document.body.style.overflow = 'hidden';
    },
    // 隐藏弹窗
    hideModal() {
      this.isVisible = false;
      this.loading = false;
      this.isSubmitting = false;
      document.body.style.overflow = '';
    },
    // 应用答案
    applyAnswers(answers) {
      const data = JSON.parse(JSON.stringify(answers || {}));
      if (!Array.isArray(data.items)) {
        data.items = [];
      }
      this.formData = data;
      this.originalData = JSON.parse(JSON.stringify(data));
    },
    // 加载数据
    async loadData(questionnaireId) {
      if (!questionnaireId) {
        this.$message.warning('问卷ID不能为空');
        this.hideModal();
        return;
      }
      this.loading = true;
      this.applyAnswers({ title: '', subTitle: '', items: [] });
      try {
        const response = await getTpcQuestionnaire(questionnaireId);
        if (response.data.code == 200) {
          const card = response.data.data || {};
          this.questionnaireId = card.id || questionnaireId;
          let answers = card.answers || {};
          if (typeof answers === 'string') {
            answers = JSON.parse(answers);
          }
          this.applyAnswers(answers);
        } else {
          this.$message.error(response.data.data || '获取问卷失败');
          this.hideModal();
        }
      } catch (error) {
        this.$message.error(error || '获取问卷失败');
        this.hideModal();
      } finally {
        this.loading = false;
      }
    },
    // 展开/折叠
    toggleExpand(item) {
      this.$set(item, 'is_expanded', !item.is_expanded);
    },
    // 选择选项
    selectOption(item, value) {
      this.$set(item, 'selected_option', value);
    },
    // 重置表单
    resetForm() {
      this.formData = JSON.parse(JSON.stringify(this.originalData));
      this.items.forEach(item => {
        this.$set(item, 'is_expanded', false);
        this.$set(item, 'selected_option', '');
      });
    },
    // 格式化源标签
    formatSourceLabel(label) {
      if (!label) return '';
      return String(label).replace(/^(\d+\.\d+)(?=\S)/, '$1 ');
    },
    // 状态文本
    statusText(status) {
      const map = {
        identified: '已识别',
        unidentified: '未识别',
      };
      return map[status] || status || '';
    },
    // 提交表单
    async submitForm() {
      if (this.isSubmitting || this.loading) return;
      const items = this.formData.items || [];
      const unfinished = items.find(item => !item.selected_option);
      if (unfinished) {
        this.$message.warning('请完成所有类别的获取难度选择');
        return;
      }
      if (!this.questionnaireId) {
        this.$message.warning('问卷ID不能为空');
        return;
      }
      this.isSubmitting = true;
      try {
        const payload = {
          answers: JSON.parse(JSON.stringify(this.formData)),
        };
        const response = await putTpcQuestionnaire(this.questionnaireId, payload);
        if (response.data.code == 200) {
          this.$emit('success', this.questionnaireId);
          this.$message.success(response.data.message || '提交成功');
          this.hideModal();
        } else {
          this.$message.error(response.data.message || '提交失败');
        }
      } catch (error) {
        this.$message.error(error || '提交失败');
      } finally {
        this.isSubmitting = false;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.back {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(33, 33, 33, 0.5);
  height: 100vh;
  z-index: 999;
}

.excel_container {
  position: fixed;
  right: 20px;
  top: 26px;
  background: var(--global-background);
  width: 1200px;
  height: calc(100vh - 52px);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  z-index: 1001;
  padding: 20px;
  transition: all 0.5s;

  .excel_header_view {
    width: 100%;
    height: 100%;
    background: #fff;
    border-radius: 20px !important;
    display: flex;
    flex-direction: column;
    padding: 20px 24px 16px;
    min-height: 0;
  }

  .excel_header_actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .el-icon-circle-close {
    font-size: 24px;
    cursor: pointer;
  }
}

.qn-header {
  flex-shrink: 0;
}

.qn-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.qn-agent-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px 0 6px;
  border-radius: 8px;
  background: #eef7ff;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
}

.qn-agent-tag img {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: cover;
}

.qn-pending-tag {
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  background: #fff1e8;
  color: #ff7a45;
  font-size: 12px;
  line-height: 26px;
  font-weight: 600;
}

.qn-title {
  font-size: 24px;
  font-weight: 700;
  color: #1d2129;
  line-height: 1.4;
}

.qn-subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #86909c;
  line-height: 1.6;
}

.qn-progress {
  margin: 18px 0 8px;
}

.qn-progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #4e5969;
  margin-bottom: 8px;
}

.qn-progress-num {
  color: #165dff;
  font-weight: 600;
}

.qn-progress-track {
  height: 6px;
  border-radius: 6px;
  background: #e8f3ff;
  overflow: hidden;
}

.qn-progress-bar {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.qn-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(117, 117, 117, 0.5) transparent;
}

.qn-body::-webkit-scrollbar {
  width: 8px;
}

.qn-body::-webkit-scrollbar-track {
  background: transparent;
}

.qn-body::-webkit-scrollbar-thumb {
  background-color: rgba(117, 117, 117, 0.5);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.qn-loading {
  height: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #86909c;
  font-size: 14px;
}

.qn-card {
  border: 1px solid #edf0f5;
  border-radius: 16px;
  padding: 18px 18px 16px;
  margin-bottom: 14px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(15, 35, 95, 0.04);
}

.qn-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qn-category {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
  line-height: 26px;
}

.qn-category.active {
  background: #3b82f6;
  color: #fff;
}

.qn-expand {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #3b82f6;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.qn-card-title {
  margin-top: 12px;
  font-size: 18px;
  font-weight: 700;
  color: #1d2129;
  line-height: 1.4;
}

.qn-card-hint {
  margin-top: 6px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #86909c;
}

.qn-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.qn-option {
  min-height: 108px;
  padding: 12px 14px;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.qn-option:hover {
  border-color: #93c5fd;
}

.qn-option.selected {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.qn-option-label {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

.qn-option-desc {
  font-size: 12px;
  line-height: 1.55;
  color: #86909c;
}

.qn-option.selected .qn-option-desc {
  color: rgba(255, 255, 255, 0.9);
}

.qn-sources {
  margin-top: 16px;
  padding-top: 4px;
}

.qn-sources-title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 600;
}

.qn-ai-icon {
  display: inline-flex;
  color: #60a5fa;
}

.qn-sources-sub {
  margin: 6px 0 12px;
  font-size: 12px;
  color: #86909c;
}

.qn-sources-empty {
  padding: 16px;
  text-align: center;
  color: #86909c;
  background: #f7f9fc;
  border-radius: 12px;
  font-size: 13px;
}

.qn-source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: #f5f8fc;
  border-radius: 12px;
}

.qn-source-label {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
}

.qn-source-data {
  margin-top: 4px;
  font-size: 12px;
  color: #86909c;
}

.qn-source-status {
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  margin-left: 16px;
}

.qn-source-status.identified {
  color: #22c55e;
}

.qn-source-status.unidentified {
  color: #f59e0b;
}

.qn-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 14px;
}

.qn-btn {
  min-width: 132px;
  height: 40px;
  padding: 0 22px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 15px;
  cursor: pointer;
  user-select: none;
}

.qn-btn-ghost {
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: #fff;
}

.qn-btn-primary {
  background: #3b82f6;
  color: #fff;
}

.qn-btn-primary.is-submitting {
  opacity: 0.7;
  cursor: not-allowed;
}

.slide-enter-active {
  animation: slideIn 0.5s forwards;
}

.slide-leave-active {
  animation: slideOut 0.5s forwards;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
</style>
